import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { readStoredUser } from "../../utils/sessionUser";
import { hasPurchasingStockPermission } from "../../utils/purchasingStockPermissions";

const signature = (businessUnitId) => {
  const user = readStoredUser();
  return JSON.stringify([user?.id, user?.active_business_unit_id, user?.permissions, businessUnitId]);
};

// A context that changes and returns to its original value still invalidates pending work.
export default function useCatalogAccess(businessUnitId) {
  const actorId = useRef(readStoredUser()?.id);
  const selectedUnit = useRef(businessUnitId);
  const mounted = useRef(true);
  const context = useRef({ key: signature(businessUnitId), epoch: 0 });
  const [, refresh] = useState(0);
  selectedUnit.current = businessUnitId;
  const synchronize = useCallback(() => {
    const key = signature(selectedUnit.current);
    if (context.current.key !== key) context.current = { key, epoch: context.current.epoch + 1 };
    return { ...context.current };
  }, []);
  synchronize();
  useEffect(() => {
    mounted.current = true;
    const update = () => { synchronize(); refresh((value) => value + 1); };
    window.addEventListener("palantir-session-updated", update);
    window.addEventListener("storage", update);
    return () => {
      mounted.current = false;
      window.removeEventListener("palantir-session-updated", update);
      window.removeEventListener("storage", update);
    };
  }, [synchronize]);
  const can = useCallback((permissions) => {
    const user = readStoredUser();
    return mounted.current && Boolean(actorId.current) && String(user?.id) === String(actorId.current) &&
      Number.isSafeInteger(Number(businessUnitId)) && Number(businessUnitId) > 0 &&
      Number(user?.active_business_unit_id) === Number(businessUnitId) && Number(selectedUnit.current) === Number(businessUnitId) &&
      (Array.isArray(permissions) ? permissions : [permissions]).every((key) => hasPurchasingStockPermission(user, key));
  }, [businessUnitId]);
  const requestConfig = useCallback((permissions) => {
    if (!can(permissions)) throw new Error("The session or business unit changed. Close this window and open it again.");
    return { headers: { "X-Pilcore-Business-Unit-Id": String(businessUnitId) }, params: { business_unit_id: businessUnitId }, pilcoreExpectedUserId: actorId.current };
  }, [businessUnitId, can]);
  const capture = useCallback(() => synchronize(), [synchronize]);
  const isCurrent = useCallback((stamp, permissions) => {
    const current = synchronize();
    return current.key === stamp.key && current.epoch === stamp.epoch && can(permissions);
  }, [synchronize, can]);
  const contextKey = `${context.current.key}:${context.current.epoch}`;
  return useMemo(() => ({ can, requestConfig, contextKey, capture, isCurrent }), [can, requestConfig, contextKey, capture, isCurrent]);
}
